/**
 * ProcurementOperationalStrip — Phase 1C cross-domain visibility.
 *
 * Read-only display over `resolveProcurementSignals()`. Two shapes:
 *   - <ProcurementOperationalStrip job /> — full strip for Job Detail.
 *   - <ProcurementOperationalChip job variant /> — compact row chip.
 *
 * NO writers. NO automation. Pure display.
 */
import type { Job } from "@/data/types";
import type {
  ProcurementSeverity,
  ProcurementOperationalSignals,
} from "@/data/procurement/procurementOperationalSignals";
import { useProcurementSignals } from "@/data/procurement/useProcurementSignals";
import { Badge } from "@/components/ui/badge";
import { Package, AlertTriangle, XCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

function severityClass(s: ProcurementSeverity): string {
  if (s === "blocker") return "bg-destructive/10 text-destructive ring-1 ring-destructive/30 border-destructive/30";
  if (s === "warn") return "bg-warning/10 text-warning ring-1 ring-warning/30 border-warning/30";
  if (s === "info") return "bg-success/10 text-success ring-1 ring-success/30 border-success/30";
  return "bg-muted/40 text-muted-foreground ring-1 ring-border/60 border-border/60";
}

function severityIcon(s: ProcurementSeverity) {
  if (s === "blocker") return <XCircle className="h-3.5 w-3.5" />;
  if (s === "warn") return <AlertTriangle className="h-3.5 w-3.5" />;
  if (s === "info") return <Package className="h-3.5 w-3.5" />;
  return <Info className="h-3.5 w-3.5" />;
}

export function ProcurementOperationalStrip({ job, className }: { job: Job; className?: string }) {
  const sig = useProcurementSignals(job);
  if (sig.procurementState === "not_required") return null;

  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 flex items-start gap-3",
        severityClass(sig.severity),
        className,
      )}
      data-testid="procurement-operational-strip"
    >
      <span className="mt-0.5 shrink-0">{severityIcon(sig.severity)}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight">{sig.summaryLabel}</p>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-foreground/80">
          <span>
            ETA: <span className="font-medium">
              {sig.etaConfidence === "vendor_confirmed"
                ? "Vendor confirmed"
                : sig.etaConfidence === "operator_estimate"
                  ? "Operator estimate"
                  : "Unknown"}
            </span>
          </span>
          {sig.allocationBlocked && (
            <span>· Allocation: <span className="font-medium">Blocked</span></span>
          )}
          {sig.partialReceipt && <span>· Partial receipt</span>}
          {sig.substitutionRisk && <span>· Substitution pending</span>}
          {sig.reconciliationDrift && (
            <span>· Drift: <span className="font-medium">{sig.reconciliationSummary}</span></span>
          )}
          {sig.missingCriticalItems.length > 0 && (
            <span>· Missing critical: <span className="font-medium">{sig.missingCriticalItems.length}</span></span>
          )}
        </div>
      </div>
    </div>
  );
}

export type ProcurementChipVariant = "readiness" | "scheduling" | "installOps" | "summary";

interface ChipProps {
  job: Job;
  variant?: ProcurementChipVariant;
  className?: string;
  hideWhenSilent?: boolean;
}

function chipFor(sig: ProcurementOperationalSignals, variant: ProcurementChipVariant) {
  if (variant === "readiness") return { sev: sig.severity, label: sig.readinessLabel };
  if (variant === "scheduling") return { sev: sig.severity, label: sig.schedulingLabel };
  if (variant === "installOps") return { sev: sig.severity, label: sig.installOpsLabel };
  return { sev: sig.severity, label: sig.summaryLabel };
}

export function ProcurementOperationalChip({
  job,
  variant = "summary",
  className,
  hideWhenSilent = false,
}: ChipProps) {
  const sig = useProcurementSignals(job);
  const silent =
    sig.procurementState === "not_required" ||
    (variant === "installOps" && sig.severity !== "warn" && sig.severity !== "blocker");
  if (hideWhenSilent && silent) return null;
  const { sev, label } = chipFor(sig, variant);
  return (
    <Badge
      variant="outline"
      className={cn("gap-1 text-[10px] font-medium", severityClass(sev), className)}
      title={label}
      data-testid={`procurement-chip-${variant}`}
    >
      {severityIcon(sev)}
      <span className="truncate max-w-[200px]">{label}</span>
    </Badge>
  );
}
